import { useCallback, useState } from "react";
import { useAppSelector } from "redux/hooks";
import { useAuth0 } from "@auth0/auth0-react";
import { StudyAuthServiceApiClient } from "apiclient/StudyAuthServiceApiClient";
import { StudyInfo } from "generated/studyauth/studyauth_pb";
import { telemetry, userNotAuthenticated } from "core/TelemetryController";

// Defines a custom effect to load data for the all studies screen.
function useAllStudiesDataLoader() {
  const auth0Config = useAppSelector((state) => state.auth0Config);
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();

  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const [studies, setStudies] = useState(new Array<StudyInfo>());
  const [cachedAt, setCachedAt] = useState(new Date());

  const loadAllStudies = useCallback(() => {
    if (isAuthenticated && auth0Config) {
      (async () => {
        try {
          setIsLoading(true);
          setHasError(false);

          const token = await getAccessTokenSilently({
            audience: auth0Config.audience,
          });

          const client = new StudyAuthServiceApiClient(
            auth0Config.audience!,
            token
          );

          // load all available studies.
          const response = await client.listStudies();
          setStudies(response.getStudiesList());
          setCachedAt(response.getCachedAt()?.toDate() ?? new Date());
        } catch (error) {
          telemetry.logError(error);
          setHasError(true);
        }

        setIsLoading(false);
      })();
    } else {
      telemetry.logError(new Error(userNotAuthenticated));
      setIsLoading(false);
      setHasError(true);
    }
  }, [isAuthenticated, auth0Config, getAccessTokenSilently]);

  return {
    isLoading,
    hasError,
    studies,
    cachedAt,
    loadAllStudies,
  };
}

export default useAllStudiesDataLoader;
